import React, { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import ProtectedRoute from '@/components/ProtectedRoute'
import { ProaResultGlobal } from '@/components/ProaResultGlobal'
import { ClientResult } from '@/components/ClientResult'
import { VarianceGauge, DonutChart } from '@/lib/components/charts'
import { useGlobalProgress } from '@/api/trpc'
import { Download } from 'lucide-react'
import { usePageTitle } from '@/stores/page-title-store'
import { exportToPDF } from '@/lib/utils/export-utils'

/**
 * Results Page - PROA Performance Dashboard
 *
 * SECURITY: This page uses tRPC endpoints that return ONLY percentages and status.
 * Raw financial values are NOT exposed to ensure directors cannot see actual numbers.
 */
function ResultsContent() {
  const { user } = useAuth()
  usePageTitle('Resultados')
  const [isExporting, setIsExporting] = useState(false)
  const currentYear = new Date().getFullYear()

  // Use progress hook for global progress (returns only percentages)
  const { data: globalProgress, isLoading } = useGlobalProgress({ year: currentYear })

  const handleExportPDF = async () => {
    try {
      setIsExporting(true)
      await exportToPDF('results-content', 'resultados-proa')
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setIsExporting(false)
    }
  }

  // Build donut chart data from progress info (percentages only)
  const donutData = globalProgress?.progress ? [
    { name: 'Acima da Meta', value: globalProgress.progress.clientsAhead },
    { name: 'No Alvo', value: globalProgress.progress.clientsOnTrack },
    { name: 'Abaixo da Meta', value: globalProgress.progress.clientsBehind },
  ] : []

  return (

      <div id="results-content" className="space-y-6 sm:space-y-8">
        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-4">
          <div className="flex items-center space-x-2 text-sm text-brand-gray">
            <div className="w-2 h-2 bg-brand-primary rounded-full animate-pulse"></div>
            <span>Dados até: {globalProgress?.asOfDate ? new Date(globalProgress.asOfDate).toLocaleDateString('pt-BR') : 'carregando...'}</span>
          </div>
          <button
            onClick={handleExportPDF}
            disabled={isExporting}
            className="flex items-center gap-2 px-3 py-2 bg-brand-primary hover:bg-brand-primary/80 text-white rounded-monumenta text-sm transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            PDF
          </button>
        </div>

        {/* Consolidated Performance Gauge - shows percentage only, no raw values */}
        {globalProgress?.progress && (
          <div className="card">
            <h2 className="font-display text-xl font-bold text-white mb-4">Performance Geral PROA {currentYear}</h2>
            <div className="flex justify-center">
              <VarianceGauge
                actual={globalProgress.progress.achievementPercent}
                target={100}
                label="Atingimento da Meta"
                size="lg"
                showPercentage={true}
              />
            </div>
            <div className="mt-4 text-center">
              <span className={`text-lg font-bold ${
                globalProgress.progress.status === 'ahead' ? 'text-success' :
                globalProgress.progress.status === 'on-track' ? 'text-warning' :
                'text-error'
              }`}>
                {globalProgress.progress.variancePercent >= 0 ? '+' : ''}{globalProgress.progress.variancePercent.toFixed(1)}% vs Meta
              </span>
            </div>
          </div>
        )}

        {/* PROA Result Global Component */}
        <ProaResultGlobal />

        {/* Client Results Component */}
        <ClientResult />

        {/* Client Status Distribution Donut Chart */}
        {donutData.length > 0 && donutData.some(d => d.value > 0) && (
          <div className="card">
            <h2 className="font-display text-xl font-bold text-white mb-4">Distribuição de Clientes por Status</h2>
            <DonutChart
              data={donutData}
              height={400}
              centerText={`${globalProgress?.progress.totalClients || 0} clientes`}
            />
          </div>
        )}
      </div>

  )
}

export default function ResultsPage() {
  return (
    <ProtectedRoute>
      <ResultsContent />
    </ProtectedRoute>
  )
}
